import React from 'react'
import AuthContainer from 'src/components/layouts/auth-container.component'
import { ButtonDeprecated, CurecallButton, ECurecallButtonAppearance } from '@honestica/curecall-ui'
import { AuthLayout } from 'src/components/layouts/auth-layout.component'
import { useTranslation } from 'react-i18next'
import { PermissionError } from 'src/types/errors'
import {
  actionToIntlTitle,
  specialAuthorizationToIntlTitle,
  subjectToIntlTitle,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useLocale } from 'src/hooks/use-locale'
import { useSession } from 'src/providers/auth.provider'

interface ErrorPageProps {
  children?: React.ReactNode
  session?: any
  error?: PermissionError
}

const PermissionErrorReport = ({ children, error }: ErrorPageProps) => {
  const { t } = useTranslation()
  const { currentUser } = useSession()
  const navigate = useLocalizedNavigate()
  const { locale } = useLocale()
  const { logout } = useSession()

  const homeHref = `/${locale}/`
  const projectSelectionHref = `/${locale}/auth/select-project`

  const goToProjectSelection = async () => {
    navigate('/auth/select-project')
  }

  const handleSignOut = () => {
    logout && logout()
  }

  const signOutCorner = (
    <button
      className="c-text-xs c-text-grey-500 c-underline hover:c-text-grey-700"
      onClick={handleSignOut}
    >
      {t('components.navigation.signout')}
    </button>
  )

  if (error?.requiredPermission) {
    return (
      <AuthLayout isError>
        <AuthContainer cornerContent={signOutCorner}>
          <h2 className="c-w-full c-text-start c-text-2xl c-font-bold c-text-black sm:c-text-start">
            {t('components.permission-error-report.insufficient-permissions')}
          </h2>
          {t('components.permission-error-report.permission-required')}{' '}
          <span className="c-font-extrabold">
            {t(actionToIntlTitle[error.requiredPermission.action])}{' '}
            {t(subjectToIntlTitle[error.requiredPermission.subject])}
          </span>
          <p className="c-my-7 c-text-start sm:c-text-start"></p>
          <p className="c-my-7 c-text-start sm:c-text-start">
            {t('components.permission-error-report.ask-your-administrator-for-permission-change')}
          </p>
          {children}
          <div className="c-mt-8 c-flex c-gap-4">
            <CurecallButton>
              <a href={homeHref}>{t('components.permission-error-report.go-to-home')}</a>
            </CurecallButton>
            <CurecallButton appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}>
              <a href={projectSelectionHref}>{t('components.permission-error-report.go-to-project-selection')}</a>
            </CurecallButton>
          </div>
        </AuthContainer>
      </AuthLayout>
    )
  }

  if (error?.requiredSpecialAuthorization) {
    return (
      <AuthLayout isError>
        <AuthContainer cornerContent={signOutCorner}>
          <h2 className="c-w-full c-text-start c-text-2xl c-font-bold c-text-black sm:c-text-start">
            {t('components.permission-error-report.insufficient-permissions')}
          </h2>
          <p className="c-my-7 c-text-start sm:c-text-start">
            {t('components.permission-error-report.special-authorization-required')}{' '}
            <span className="c-font-extrabold">
              {t(specialAuthorizationToIntlTitle[error.requiredSpecialAuthorization])}
            </span>
          </p>
          <p className="c-my-7 c-text-start sm:c-text-start">
            {t('components.permission-error-report.ask-your-administrator-for-permission-change')}
          </p>

          {children}

          <div className="c-mt-8 c-flex c-gap-4">
            <CurecallButton>
              <a href={homeHref}>{t('components.permission-error-report.go-to-home')}</a>
            </CurecallButton>
            <CurecallButton appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}>
              <a href={projectSelectionHref}>{t('components.permission-error-report.go-to-project-selection')}</a>
            </CurecallButton>
          </div>
        </AuthContainer>
      </AuthLayout>
    )
  }

  if (error?.isNetworkBlocked) {
    return (
      <AuthLayout isError>
        <AuthContainer>
          <h2 className="c-w-full c-text-start c-text-2xl c-font-bold c-text-black sm:c-text-start">
            {t('components.permission-error-report.blocked-network.title')}
          </h2>
          <p className="c-my-7 c-text-start sm:c-text-start">
            {t('components.permission-error-report.blocked-network.description')}
          </p>
          <p className="c-my-7 c-text-start sm:c-text-start">
            {t('components.permission-error-report.blocked-network.recommendation')}
          </p>
          {children}
          <div className="c-mt-8 c-flex c-gap-4">
            <CurecallButton appearance={ECurecallButtonAppearance['PRIMARY']} onClick={handleSignOut}>
              {t('components.navigation.signout')}
            </CurecallButton>
            {currentUser?.hasMultiProjects && (
              <CurecallButton
                appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
                onClick={goToProjectSelection}
              >
                {t('components.permission-error-report.blocked-network.change-project')}
              </CurecallButton>
            )}
          </div>
        </AuthContainer>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout isError>
      <AuthContainer cornerContent={signOutCorner}>
        <h2 className="c-w-full c-text-start c-text-2xl c-font-bold c-text-black sm:c-text-start">
          {t('components.permission-error-report.insufficient-permissions')}
        </h2>
        <div className="c-mt-8 c-flex c-gap-4">
          <ButtonDeprecated variant="rounded" className=" c-mb-20" size="xl">
            <a href={homeHref}>{t('components.permission-error-report.go-to-home')}</a>
          </ButtonDeprecated>
          <CurecallButton appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}>
            <a href={projectSelectionHref}>{t('components.permission-error-report.go-to-project-selection')}</a>
          </CurecallButton>
        </div>
      </AuthContainer>
    </AuthLayout>
  )
}

export default PermissionErrorReport
